import { TailSpin } from 'react-loader-spinner'
import { Link, useSearchParams } from 'react-router-dom'
import SearchInput from '../../../components/SearchInput'
import { Blue300 } from '../../../lib/colors'
import { useGetShippingTestsByQueryQuery } from '../../../generated/urql.anonymous'
import { useI18n } from '../../../provider/i18n'
import ProfileSelectorModal, {
  ProfileSelectorModalType,
} from '../../../components/ProfileSelectorModal'
import { useCallback, useContext, useRef } from 'react'
import { AuthContext } from '../../../provider/auth'
import {
  BasketContext,
  isShippingTestBasketItem,
} from '../../../provider/basket'
import { profilesSubscriptionAtom } from '../../../store/subscription.atoms'
import { useAtomValue } from 'jotai'
import { SearchTestFragment } from '../../../generated/urql.client'
import { TestItem } from '../../../components/TestItem'

const SearchShippingTests = () => {
  const [params] = useSearchParams()
  const i18n = useI18n()
  const basket = useContext(BasketContext)
  const { isLogged } = useContext(AuthContext)
  const query = params.get('q')
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [{ data: testsData, fetching: isFetching }] =
    useGetShippingTestsByQueryQuery({
      pause: !query,
      variables: {
        limit: 1000,
        query: query || '',
      },
    })

  const onTestAddClick = useCallback(
    (test: SearchTestFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!isLogged || profilesCount === 0) {
        basket.addItem({ test, shouldShipToClient: true })
      } else if (profilesCount === 1) {
        basket.addItem({
          test,
          profile: profilesData?.client_profile[0],
          shouldShipToClient: true,
        })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ test, profile, shouldShipToClient: true })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isShippingTestBasketItem(item) &&
                item.test.id === test.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [isLogged, basket, profilesData?.client_profile]
  )

  const tests = testsData?.search_test_shipping || []

  return (
    <div>
      <SearchInput />
      <div className="text-blue-700 my-5 flex gap-1 items-center">
        <span>
          <Link to="/shipping-tests">
            {i18n.t('screens.catalog.index.shippingTests.header')}
          </Link>
          {' > '}
          {i18n.t('screens.catalog.search.breadcrumbs.searchResults', {
            query: query || '',
          })}
        </span>
        {isFetching && <TailSpin color={Blue300} height={16} width={16} />}
      </div>
      <ul className="md:mx-5 flex flex-col gap-5 mb-10">
        {tests.map((test) => (
          <li key={test.id}>
            <TestItem
              test={test}
              variant="green"
              onTestAddClick={() => onTestAddClick(test)}
              showAddButton={
                isLogged
                  ? basket.items
                      .filter(isShippingTestBasketItem)
                      .filter(({ test: { id } }) => test.id === id).length <
                    (profilesData?.client_profile.length || 0)
                  : !basket.items.find(
                      (item) =>
                        isShippingTestBasketItem(item) &&
                        test.id === item.test.id
                    )
              }
            />
          </li>
        ))}
      </ul>
      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}

export default SearchShippingTests
